import { Injectable } from '@angular/core';

export interface AdminGridCacheEntry {
  data: any;
  timestamp: number;
}

/**
 * Caches grid API responses for admin screens so that revisiting a tab
 * does not trigger a new HTTP call.  Keyed by a caller-supplied string
 * (typically the route path or action_id + grid_id composite).
 *
 * The cache lives in memory for the duration of the SPA session.
 * Individual entries or the whole cache can be cleared on demand
 * (e.g. after a save, after client change, etc.).
 */
@Injectable({ providedIn: 'root' })
export class AdminGridCacheService {
  private static readonly MAX_ENTRIES = 20;
  private cache = new Map<string, AdminGridCacheEntry>();

  get(key: string, maxAgeMs?: number): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (maxAgeMs && Date.now() - entry.timestamp > maxAgeMs) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  set(key: string, data: any): void {
    if (this.cache.size >= AdminGridCacheService.MAX_ENTRIES && !this.cache.has(key)) {
      const oldest = this.cache.keys().next().value;
      if (oldest) this.cache.delete(oldest);
    }
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }
}
